import React, { useEffect, useState } from 'react';
import { MessageSquare, Mail, Copy, Check, UserPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useI18n } from '../i18n/I18nProvider';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2c-1.5 0-3-.4-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.2-.4.7-1.4.1-.2 0-.3 0-.4l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 11.9 11.9 0 0 0 4.6 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3Z" /></svg>
);

export default function InviteFriend({ variant = 'dark', testid = 'invite-friend-btn' }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const url = `${window.location.origin}/#rsvp`;
  const initial = t.invite.message.replace('{url}', url);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(initial);
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (open) setMsg(initial); }, [open, initial]);

  const enc = encodeURIComponent;
  const links = {
    sms: `sms:?&body=${enc(msg)}`,
    email: `mailto:?subject=${enc(t.invite.subject)}&body=${enc(msg)}`,
    whatsapp: `https://wa.me/?text=${enc(msg)}`,
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(msg); } catch (_) { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast({ title: t.invite.copied, description: t.invite.copiedText });
  };

  const trigger = variant === 'dark'
    ? 'border border-[#f5efe3]/30 text-[#f5efe3] hover:bg-[#f5efe3]/10'
    : 'bg-[#1f3a2a] hover:bg-[#2e5340] text-[#f5efe3]';
  const opt = 'flex flex-col items-center justify-center gap-1.5 rounded-xl border border-[#e5dcc5] bg-[#faf6ea] hover:bg-[#f0e9d8] hover:border-[#2e5340] text-[#1f3a2a] text-xs font-medium py-3 transition-colors';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={testid} className={`inline-flex items-center gap-2 rounded-full h-11 px-5 text-sm font-medium transition-colors ${trigger}`}>
          <UserPlus size={17} /> {t.invite.button}
        </button>
      </DialogTrigger>
      <DialogContent data-testid="invite-dialog" className="bg-white border-[#e5dcc5] rounded-3xl w-[calc(100%-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1f3a2a]">{t.invite.title}</DialogTitle>
          <DialogDescription className="text-[#4a5a4e]">{t.invite.sub}</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="invite-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} className="bg-[#faf6ea] border-[#e5dcc5] rounded-xl focus-visible:ring-[#2e5340] min-h-[150px] text-sm" />
        <div className="grid grid-cols-4 gap-2">
          <a href={links.sms} data-testid="invite-sms-link" className={opt}><MessageSquare size={16} /> {t.invite.text}</a>
          <a href={links.email} data-testid="invite-email-link" className={opt}><Mail size={16} /> {t.invite.email}</a>
          <a href={links.whatsapp} target="_blank" rel="noreferrer" data-testid="invite-whatsapp-link" className={opt}><WhatsAppIcon /> {t.invite.whatsapp}</a>
          <button type="button" onClick={copy} data-testid="invite-copy-btn" className={opt}>{copied ? <Check size={16} /> : <Copy size={16} />} {copied ? t.invite.copied : t.invite.copy}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
